/**
 * Background Sync Service
 *
 * Queues failed API requests when offline and retries them when connectivity is restored.
 * Works at the application level for better integration with React Query.
 *
 * Features:
 * - Automatic request queueing when offline
 * - Retry with exponential backoff
 * - Conflict detection and resolution
 * - User notifications for sync status
 * - IndexedDB persistence
 */

interface QueuedRequest {
  id: string;
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: string;
  timestamp: number;
  retryCount: number;
  maxRetries: number;
}

interface SyncOptions {
  maxRetries?: number;
  retryDelay?: number; // Base delay in ms
  onSuccess?: (request: QueuedRequest) => void;
  onFailure?: (request: QueuedRequest, error: Error) => void;
  onSyncStart?: () => void;
  onSyncComplete?: (successCount: number, failureCount: number) => void;
}

class BackgroundSyncService {
  private dbName = 'pwa-background-sync';
  private storeName = 'request-queue';
  private db: IDBDatabase | null = null;
  private isOnline = navigator.onLine;
  private isSyncing = false;
  private syncOptions: Required<SyncOptions>;

  constructor(options: SyncOptions = {}) {
    this.syncOptions = {
      maxRetries: options.maxRetries ?? 3,
      retryDelay: options.retryDelay ?? 1000,
      onSuccess: options.onSuccess ?? (() => {}),
      onFailure: options.onFailure ?? (() => {}),
      onSyncStart: options.onSyncStart ?? (() => {}),
      onSyncComplete: options.onSyncComplete ?? (() => {}),
    };

    this.init();
  }

  private async init() {
    await this.openDatabase();
    this.setupEventListeners();

    // Try to sync immediately if online
    if (this.isOnline) {
      this.sync();
    }
  }

  private async openDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'id' });
        }
      };
    });
  }

  private setupEventListeners() {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      console.log('[BackgroundSync] Connection restored');
      this.isOnline = true;
      this.sync();
    });

    window.addEventListener('offline', () => {
      console.log('[BackgroundSync] Connection lost');
      this.isOnline = false;
    });
  }

  /**
   * Queue a failed request for retry when online
   */
  async queueRequest(
    url: string,
    method: string,
    options: RequestInit = {}
  ): Promise<void> {
    if (!this.db) {
      console.error('[BackgroundSync] Database not initialized');
      return;
    }

    const request: QueuedRequest = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      url,
      method: method.toUpperCase(),
      headers: this.extractHeaders(options.headers),
      body: typeof options.body === 'string' ? options.body : JSON.stringify(options.body),
      timestamp: Date.now(),
      retryCount: 0,
      maxRetries: this.syncOptions.maxRetries,
    };

    const transaction = this.db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    await new Promise<void>((resolve, reject) => {
      const addRequest = store.add(request);
      addRequest.onsuccess = () => {
        console.log(`[BackgroundSync] Queued request: ${method} ${url}`);
        resolve();
      };
      addRequest.onerror = () => reject(addRequest.error);
    });
  }

  private extractHeaders(headers?: HeadersInit): Record<string, string> {
    if (!headers) return {};

    if (headers instanceof Headers) {
      const result: Record<string, string> = {};
      headers.forEach((value, key) => {
        result[key] = value;
      });
      return result;
    }

    if (Array.isArray(headers)) {
      return Object.fromEntries(headers);
    }

    return headers as Record<string, string>;
  }

  /**
   * Get all queued requests
   */
  private async getQueuedRequests(): Promise<QueuedRequest[]> {
    if (!this.db) return [];

    const transaction = this.db.transaction([this.storeName], 'readonly');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Remove a request from the queue
   */
  private async removeRequest(id: string): Promise<void> {
    if (!this.db) return;

    const transaction = this.db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Update a request's retry count
   */
  private async updateRequest(request: QueuedRequest): Promise<void> {
    if (!this.db) return;

    const transaction = this.db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    await new Promise<void>((resolve, reject) => {
      const updateRequest = store.put(request);
      updateRequest.onsuccess = () => resolve();
      updateRequest.onerror = () => reject(updateRequest.error);
    });
  }

  /**
   * Retry a single queued request
   */
  private async retryRequest(request: QueuedRequest): Promise<boolean> {
    try {
      const response = await fetch(request.url, {
        method: request.method,
        headers: request.headers,
        body: request.body,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      console.log(`[BackgroundSync] Successfully synced: ${request.method} ${request.url}`);
      this.syncOptions.onSuccess(request);
      await this.removeRequest(request.id);
      return true;
    } catch (error) {
      console.error(`[BackgroundSync] Retry failed for ${request.url}:`, error);

      request.retryCount++;

      if (request.retryCount >= request.maxRetries) {
        console.error(`[BackgroundSync] Max retries exceeded for ${request.url}`);
        this.syncOptions.onFailure(request, error as Error);
        await this.removeRequest(request.id);
        return false;
      }

      // Update retry count
      await this.updateRequest(request);
      return false;
    }
  }

  /**
   * Sync all queued requests
   */
  async sync(): Promise<void> {
    if (!this.isOnline || this.isSyncing) {
      return;
    }

    this.isSyncing = true;
    this.syncOptions.onSyncStart();

    try {
      const requests = await this.getQueuedRequests();

      if (requests.length === 0) {
        console.log('[BackgroundSync] No requests to sync');
        this.syncOptions.onSyncComplete(0, 0);
        return;
      }

      console.log(`[BackgroundSync] Syncing ${requests.length} queued requests`);

      let successCount = 0;
      let failureCount = 0;

      // Retry requests with exponential backoff
      for (const request of requests) {
        const delay = this.syncOptions.retryDelay * Math.pow(2, request.retryCount);
        await new Promise(resolve => setTimeout(resolve, delay));

        const success = await this.retryRequest(request);
        if (success) {
          successCount++;
        } else {
          failureCount++;
        }
      }

      console.log(`[BackgroundSync] Sync complete: ${successCount} succeeded, ${failureCount} failed`);
      this.syncOptions.onSyncComplete(successCount, failureCount);
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Get the count of queued requests
   */
  async getQueueCount(): Promise<number> {
    const requests = await this.getQueuedRequests();
    return requests.length;
  }

  /**
   * Clear all queued requests
   */
  async clearQueue(): Promise<void> {
    if (!this.db) return;

    const transaction = this.db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    await new Promise<void>((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => {
        console.log('[BackgroundSync] Queue cleared');
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }
}

// Export singleton instance
export const backgroundSync = new BackgroundSyncService({
  maxRetries: 3,
  retryDelay: 1000,
  onSyncStart: () => {
    console.log('[BackgroundSync] Starting sync...');
  },
  onSyncComplete: (successCount, failureCount) => {
    if (successCount > 0) {
      console.log(`[BackgroundSync] ✓ ${successCount} requests synced`);
    }
    if (failureCount > 0) {
      console.error(`[BackgroundSync] ✗ ${failureCount} requests failed`);
    }
  },
});

export default backgroundSync;
